import { useMemo, useState } from 'react';

import { savedViewsApi, type SavedView } from '@/api/queries/workspace';
import { Icon } from '@/components/ui';
import { cn } from '@/utils/cn';

import { SaveViewModal } from './SaveViewModal';
import type { ContactsSearch } from './searchTypes';

interface SavedViewTabsProps {
  search: ContactsSearch;
  total: number;
  onSelect: (next: ContactsSearch) => void;
}

const ALWAYS_PRESENT_KEYS: (keyof ContactsSearch)[] = ['page', 'pageSize', 'new'];

function searchFilterKeys(s: ContactsSearch): (keyof ContactsSearch)[] {
  return (Object.keys(s) as (keyof ContactsSearch)[]).filter(
    (k) => !ALWAYS_PRESENT_KEYS.includes(k) && s[k] !== undefined,
  );
}

function matchesApi(view: SavedView, search: ContactsSearch): boolean {
  const filters = view.filters as Partial<ContactsSearch>;
  const keys = Object.keys(filters) as (keyof ContactsSearch)[];
  if (keys.length === 0) return searchFilterKeys(search).length === 0;
  return (
    keys.every((k) => search[k] === filters[k]) &&
    searchFilterKeys(search).every((k) => keys.includes(k))
  );
}

export function SavedViewTabs({ search, total, onSelect }: SavedViewTabsProps) {
  const { data: apiViews = [] } = savedViewsApi.useList();
  const deleteView = savedViewsApi.useDelete();
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Global tabs are the tenant's workspace-scoped views — configured by an admin
  // under Admin → Tenants → Views, or saved here via "New view" (shared scope).
  // There are no built-in presets; a tenant starts with none. User-scoped views
  // live in the sidebar instead.
  const workspaceViews = useMemo(
    () => apiViews.filter((v) => v.scope === 'workspace'),
    [apiViews],
  );

  const activeApiId = useMemo(
    () => workspaceViews.find((v) => matchesApi(v, search))?.id,
    [workspaceViews, search],
  );

  const hasUnsavedFilters = !activeApiId && searchFilterKeys(search).length > 0;

  const handleDelete = (view: SavedView) => {
    if (confirm(`Delete shared view "${view.name}"? This can't be undone.`)) {
      deleteView.mutate(view.id);
    }
  };

  return (
    <>
      <div className="views-bar" role="tablist" aria-label="Saved views">
        {workspaceViews.map((view) => {
          const isActive = view.id === activeApiId;
          return (
            <span key={view.id} className="view-tab-wrap">
              <button
                type="button"
                role="tab"
                aria-selected={isActive}
                className={cn('view-tab', isActive && 'active')}
                onClick={() =>
                  onSelect({
                    page: 0,
                    pageSize: search.pageSize,
                    ...(view.filters as Partial<ContactsSearch>),
                  })
                }
              >
                {view.color && (
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: view.color,
                    }}
                  />
                )}
                {view.name}
                {isActive && total > 0 && (
                  <span className="vt-count">{total.toLocaleString()}</span>
                )}
              </button>
              <button
                type="button"
                className="view-tab-del"
                aria-label={`Delete view ${view.name}`}
                title="Delete view"
                onClick={() => handleDelete(view)}
              >
                <Icon name="x" size={10} />
              </button>
            </span>
          );
        })}

        <button
          type="button"
          className="view-tab-add"
          title={
            hasUnsavedFilters
              ? 'Save current filter as a new view'
              : 'Save the current contacts view'
          }
          onClick={() => setShowSaveModal(true)}
        >
          <Icon name="plus" size={12} />
          New view
        </button>
      </div>

      <SaveViewModal
        open={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        currentSearch={search}
      />
    </>
  );
}
